import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { List } from "immutable";
import { GeometryUtils } from "../../utils/export";
import Ruler from "./ruler";
import {
  MODE_DRAGGING_SQUARE,
  MODE_DRAWING_SQUARE,
  MODE_DRAWING_LINE,
  MODE_IDLE,
} from "../../constants";

export default function Line({ line, layer, scene, catalog }, { store }) {
  let mode = store.getState().getIn(["react-planner"]).get("mode");
  let draggingSupport = store
    .getState()
    .getIn(["react-planner"])
    .get("draggingSupport");

  let vertex0 = layer.vertices.get(line.vertices.get(0));
  let vertex1 = layer.vertices.get(line.vertices.get(1));
  // Missing vertex during split-bridge create/merge → skip render (never white-screen).
  if (!vertex0 || !vertex1) return null;
  if (vertex0.id === vertex1.id || GeometryUtils.samePoints(vertex0, vertex1))
    return null; //avoid 0-length lines

  // Use stored vertex order — do NOT swap by x.
  // Swapping nearly-vertical walls (x1≈x2) flipped the SVG parent translate/rotate
  // and produced stair-step / ghost walls ("translate parent" bugs).
  let x1 = vertex0.x;
  let y1 = vertex0.y;
  let x2 = vertex1.x;
  let y2 = vertex1.y;

  let length = GeometryUtils.pointsDistance(x1, y1, x2, y2);
  if (!Number.isFinite(length) || length < 1) return null;

  let angle = GeometryUtils.angleBetweenTwoPointsAndOrigin(x1, y1, x2, y2);
  if (!Number.isFinite(angle)) return null;
  let renderedHoles = (line.holes || new List())
    .filter((holeID) => {
      let hole = layer.holes.get(holeID);
      if (!hole || !hole.visible) return false;
      // FIX: Skip holes whose line property doesn't match current line
      // This prevents V-shape artifacts from holes with corrupted line associations
      if (hole.line && hole.line !== line.id) return false;
      return true;
    })
    .map((holeID) => {
      let hole = layer.holes.get(holeID);
      let startAt = length * hole.offset;

      // FIX: Pass correct selectedLayer to hole render2D so doors/windows
      // find their parent wall correctly when multiple floors exist
      let sceneForHole = scene.set('selectedLayer', layer.id);

      let renderedHole = null;
      try {
        renderedHole = catalog
          .getElement(hole.type)
          .render2D(hole, layer, sceneForHole);
      } catch(e) {
        return null;
      }

      if (!renderedHole) return null;

      return (
        <g
          key={holeID}
          transform={`translate(${startAt}, 0)`}
          data-element-root
          data-prototype={hole.prototype}
          data-id={hole.id}
          data-selected={hole.selected}
          data-layer={layer.id}
        >
          {renderedHole}
        </g>
      );
    })
    .filter(h => h !== null);

  let thickness = line.getIn(["properties", "thickness"]);
  if (typeof thickness !== "number" || !Number.isFinite(thickness)) {
    thickness = 40;
  }
  let half_thickness = thickness / 2;

  let isSquareLine =
    mode === MODE_DRAWING_SQUARE &&
    draggingSupport &&
    draggingSupport.get("squareLines") &&
    draggingSupport.get("squareLines").some((l) => l.id === line.id);

  let renderedLine = null;
  try {
    renderedLine = catalog.getElement(line.type).render2D(
      line,
      layer,
      // FIX: add null-check for squareLines to prevent crash when draggingSupport
      // is reset (e.g. after layer switch) - crash made walls render invisible.
      // Also pass MODE_FINISHING_PLAN through so wall renders correctly in download screen.
      isSquareLine
        ? MODE_DRAWING_SQUARE
        : mode === MODE_DRAWING_LINE
        ? MODE_DRAWING_LINE
        : mode === "MODE_FINISHING_PLAN"
        ? "MODE_FINISHING_PLAN"
        : MODE_IDLE
    );
  } catch (e) {
    return null;
  }

  let renderedRuler = line.selected || isSquareLine ? (
      <Ruler
        unit={scene.unit}
        length={length}
        transform={`translate(0, ${half_thickness + 10} )`}
      />
    ) : null;

  return (
    <g
      transform={`translate(${x1}, ${y1}) rotate(${angle}, 0, 0)`}
      data-element-root
      data-prototype={line.prototype}
      data-id={line.id}
      data-selected={line.selected}
      data-layer={layer.id}
    >
      {renderedRuler}
      {renderedLine}
      {renderedHoles}
    </g>
  );
}

Line.propTypes = {
  line: PropTypes.object.isRequired,
  layer: PropTypes.object.isRequired,
  scene: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
};
Line.contextTypes = {
  store: PropTypes.object.isRequired,
};
